import {
  FileText, Calendar, Building2, DollarSign, AlertTriangle, CheckCircle, ChevronRight 
} from 'lucide-react';
import {
  Card, CardContent, CardHeader, CardTitle 
} from '@/components/ui/card';
import type {
  Invoice, MatchStatus 
} from '../../types';
import {
  INVOICE_STATUS_LABELS, INVOICE_STATUS_COLORS 
} from '../../types';

function MatchStatusIcon({ status }: { readonly status: MatchStatus }) {
  if (status === 'matched') {
    return <CheckCircle className="h-4 w-4 text-green-500" />;
  }
  if (status === 'mismatch') {
    return <AlertTriangle className="h-4 w-4 text-red-500" />;
  }
  return <div className="h-4 w-4 rounded-full border border-muted-foreground" />;
}

interface InvoiceCardProps {readonly invoice: Invoice;}

export function InvoiceCard({ invoice }: InvoiceCardProps) {
  const statusColor = INVOICE_STATUS_COLORS[invoice.status];
  const isOverdue = new Date(invoice.dueDate) < new Date() && invoice.status !== 'paid';

  return (
    <Card className="hover:shadow-md transition-shadow group">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-muted-foreground" />
            <CardTitle className="text-base font-medium">{invoice.invoiceNumber}</CardTitle>
          </div>
          <div className="flex items-center gap-2">
            <span className={`rounded-full px-2 py-1 text-xs font-medium ${statusColor}`}>
              {INVOICE_STATUS_LABELS[invoice.status]}
            </span>
            <ChevronRight className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
          </div>
        </div>
        <p className="text-sm text-muted-foreground">{invoice.supplierName}</p>
      </CardHeader>
      <CardContent className="space-y-3">
        {invoice.poNumber && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Building2 className="h-4 w-4" />
            <span>PO: {invoice.poNumber}</span>
          </div>
        )}

        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Calendar className="h-4 w-4" />
          <span>Invoice Date: {new Date(invoice.invoiceDate).toLocaleDateString()}</span>
        </div>

        <div className={`flex items-center gap-2 text-sm ${isOverdue ? 'text-red-600' : 'text-muted-foreground'}`}>
          {isOverdue ? <AlertTriangle className="h-4 w-4" /> : <Calendar className="h-4 w-4" />}
          <span>Due: {new Date(invoice.dueDate).toLocaleDateString()}</span>
          {isOverdue && <span className="text-xs font-medium">(Overdue)</span>}
        </div>

        {invoice.matchStatus && (
          <div className="flex items-center gap-2 text-sm">
            <MatchStatusIcon status={invoice.matchStatus} />
            <span className="text-muted-foreground">
              3-Way Match: {invoice.matchStatus}
              {invoice.matchVariance !== undefined && invoice.matchVariance > 0 && (
                <span className="text-red-600 ml-1">(${invoice.matchVariance.toFixed(2)} variance)</span>
              )}
            </span>
          </div>
        )}

        <div className="pt-2 border-t">
          <p className="text-xs text-muted-foreground mb-2">Line Items ({invoice.lines.length}):</p>
          <div className="space-y-1">
            {invoice.lines.slice(0, 3).map((line) => (
              <div key={line.lineId} className="flex items-center justify-between text-sm">
                <span className="truncate flex-1">{line.itemName}</span>
                <span className="text-muted-foreground ml-2">${line.totalPrice.toFixed(2)}</span>
              </div>
            ))}
            {invoice.lines.length > 3 && (
              <p className="text-xs text-muted-foreground">+{invoice.lines.length - 3} more items</p>
            )}
          </div>
        </div>

        {invoice.notes && (
          <p className="text-sm text-muted-foreground italic border-l-2 border-muted pl-2">
            {invoice.notes}
          </p>
        )}

        <div className="flex items-center justify-between pt-2 border-t">
          <div className="flex items-center gap-1 text-primary">
            <DollarSign className="h-4 w-4" />
            <span className="text-lg font-bold">{invoice.total.toLocaleString()}</span>
            <span className="text-xs text-muted-foreground">{invoice.currency}</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
